/**
 * Плановые цены — матрица культура × месяц (AK-Planning, экран 3 прототипа
 * `docs/superpowers/prototypes/2026-08-06-spp-scenarios/index.html`).
 *
 * Спека `docs/superpowers/specs/2026-08-06-spp-scenarios-v1.md`
 * («Решения founder» §1): матрица культура × месяц редактируется руками,
 * «Импорт из файла» — только предзаполнение (не заменяет проверку
 * человеком). Жёлтая подсветка ячейки = разошлась с последним импортом
 * (или введена руками без импорта вовсе) — `is_manual` с бэка.
 *
 * Термины — канон v3 (`docs/superpowers/specs/2026-08-07-season-terminology-v3.md`):
 * контейнер называется «Бюджет сезона {год}», набор справочников — «Бюджетные
 * справочники». «План сезона» в UI не пишем.
 *
 * Страница САМОДОСТАТОЧНАЯ (не завязана на хаб `/planning`, который переезжает
 * на v2 в #692/#693): год берёт из url, сезон резолвит сама через
 * `listSeasonCards`, если сезон ещё не материализован — просит сначала открыть
 * бюджет сезона на странице «Планирование сезона».
 *
 * Блок «Логистика по направлениям» — read-only плашка без данных: схема
 * `company_sale_prices` не несёт оси «направление» (см.
 * `docs/superpowers/specs/2026-08-06-sale-prices-form-open-questions.md`).
 * Выдуманных цифр и «редактируемого черновика, который никуда не уходит»
 * на экране нет — сознательно (вердикт head-web).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Info, Upload } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatMoneyNumber, formatPrice } from '@/lib/planning/money';
import { listSeasonCards } from '@/lib/planning/api';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from './SeasonBudgetStates';
import {
  MONTH_LABELS_SHORT,
  bulkUpsertSalePrices,
  computeAverage,
  getSalePriceMatrix,
  importSalePrices,
  parsePriceInput,
  type CultureRow,
  type ImportResult,
  type PriceCellPatch,
} from '@/lib/planning/salePrices';

const QUERY_KEY = (seasonId: string) => ['planning', 'sale-prices', seasonId];

/**
 * Разобрать CSV-вставку формата «Культура;цена янв;...;цена дек» (одна
 * строка = культура) в `ImportRow[]`. Пустая ячейка = пропустить месяц.
 * Разделитель — `;` или `\t` (Excel copy-paste даёт табуляцию).
 */
export function parseImportText(text: string): { crop_name: string; months: Record<number, number> }[] {
  const rows: { crop_name: string; months: Record<number, number> }[] = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const cells = line.split(/[;\t]/).map((c) => c.trim());
    const cropName = cells[0];
    if (!cropName) continue;
    const months: Record<number, number> = {};
    for (let i = 1; i <= 12 && i < cells.length; i++) {
      const raw = cells[i]?.replace(',', '.');
      if (raw && raw !== '—' && raw !== '-') {
        const v = Number(raw);
        if (Number.isFinite(v)) months[i] = v;
      }
    }
    rows.push({ crop_name: cropName, months });
  }
  return rows;
}

function ImportDialog({
  seasonId,
  onClose,
  onImported,
}: {
  seasonId: string;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
}) {
  const [text, setText] = useState('');
  const mutation = useMutation({
    mutationFn: () => importSalePrices(seasonId, parseImportText(text)),
    onSuccess: (result) => onImported(result),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Импорт из файла (предзаполнение)</DialogTitle>
          <DialogDescription>
            Вставьте строки в формате «Культура; цена янв; цена фев; …; цена дек» (12
            месяцев, разделитель — точка с запятой или табуляция из Excel). Импорт только
            предзаполняет пустые ячейки и ячейки, которые ещё не правили руками — правки
            поверх импорта не перезаписываются.
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'Озимая пшеница;24350;27200;;;;;;27800;;;;28100\nПодсолнечник;;;;;;;;;42000;41500;;'}
          className="h-48 w-full rounded-md border border-input bg-background p-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {mutation.isError && (
          <p className="text-sm text-destructive">Не удалось импортировать. Проверьте формат и попробуйте снова.</p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!text.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            <Upload className="mr-2 h-4 w-4" />
            {mutation.isPending ? 'Импортируем…' : 'Импортировать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Сырое значение для режима правки: без разрядов и запятой. */
const rawPriceValue = (v: number | null) => (v === null ? '' : String(v));

export function PriceCellInput({
  crop,
  month,
  hasError,
  onCommit,
}: {
  crop: CultureRow;
  month: number;
  /** Последняя отправка этой ячейки не сохранилась — подсветить `destructive`. */
  hasError: boolean;
  /** Промис отправки: по нему ячейка решает, когда уйти из режима правки. */
  onCommit: (cropItemId: string, month: number, value: number | null) => Promise<unknown>;
}) {
  const cell = crop.months.find((m) => m.month === month);
  const serverValue = cell?.price_no_vat ?? null;

  // Два режима отображения (AK-704): не в фокусе — «24 350,50» с разделителем
  // разрядов, в фокусе — сырые цифры для набора. Форматировать во время ввода
  // нельзя: курсор прыгает вокруг пробелов на каждый символ.
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rawPriceValue(serverValue));
  const [invalid, setInvalid] = useState(false);
  // «То, что мы только что сохранили» — держит цену без мигания СТАРЫМ
  // значением между ответом сервера и приходом свежей матрицы. Механика
  // 1:1 с ячейкой бюджетного справочника (`BudgetItemsTable`, ревью #850 B2):
  // две ячейки-близнеца обязаны вести себя одинаково, иначе «усталость»,
  // которую там чинили, живёт здесь.
  //
  // `display` может быть `null` (цену очистили) — потому оверрайд завёрнут в
  // объект, а не голое `number | null`: иначе «очищено локально» неотличимо
  // от «оверрайда нет вовсе».
  const [justSaved, setJustSaved] = useState<{ display: number | null; from: number | null } | null>(
    null,
  );

  useEffect(() => {
    if (!editing) setValue(rawPriceValue(serverValue));
    // Снимаем оверрайд ПО ФАКТУ обновления матрицы (проп ушёл от значения,
    // которое было ДО сохранения), а не по равенству с отправленным: равенство
    // залипло бы навсегда, если сервер вернёт другое число — а с квантованием
    // цен до сотых (AK-704) это норма: прислали 24350.506, бэк ответил 24350.51.
    if (justSaved && serverValue !== justSaved.from) setJustSaved(null);
  }, [serverValue, editing, justSaved]);

  const isManual = cell?.is_manual ?? false;
  // Ошибка сохранения важнее «правки руками»: юзер должен видеть, что цифра не уехала.
  const stateClass = hasError
    ? 'border-destructive bg-destructive/10'
    : isManual
      ? 'border-accent-brand bg-accent-brand/10'
      : '';

  const displayPrice = justSaved ? justSaved.display : serverValue;

  return (
    <Input
      ref={inputRef}
      // Не `type="number"`: браузер отдаёт пустую строку на «24350,5» и дробные
      // руками не вводятся. Запятую нормализует `parsePriceInput`.
      type="text"
      inputMode="decimal"
      value={editing ? value : formatMoneyNumber(displayPrice)}
      aria-invalid={hasError || invalid || undefined}
      onFocus={() => {
        setEditing(true);
        setJustSaved(null);
        setValue(rawPriceValue(serverValue));
      }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const parsed = parsePriceInput(value);
        if (parsed === undefined) {
          // Мусор остаётся в поле как есть (не форматируем, не откатываем) —
          // человек должен видеть, что именно он набрал, и поправить это.
          setInvalid(true);
          return;
        }
        setInvalid(false);
        if (parsed === serverValue) {
          setEditing(false);
          return;
        }
        // Значение ДО сохранения — по нему эффект поймёт, что матрица реально
        // обновилась (а не просто случайно совпала).
        const savedFrom = serverValue;
        // ОСТАЁМСЯ в режиме правки (сырое «24350» на экране) до ответа сервера:
        // мутация не оптимистична, и уход из режима правки раньше ответа
        // засветил бы СТАРУЮ цену на весь round-trip.
        void onCommit(crop.crop_item_id, month, parsed)
          .then(() => {
            setJustSaved({ display: parsed, from: savedFrom });
            // Пользователь мог вернуться в поле и набирать заново, пока ответ
            // на ПРЕЖНЮЮ отправку летел — не выбиваем его из нового ввода.
            if (document.activeElement !== inputRef.current) setEditing(false);
          })
          .catch(() => {
            // Набранное ОСТАЁТСЯ на экране — ровно это обещает карточка отказа
            // сверху страницы («значения остались только на экране — они
            // подсвечены красным»), и ячейка уже красная через `hasError`.
            // Поэтому, в отличие от бюджетного справочника, серверное значение
            // сюда НЕ возвращаем и из режима правки не выходим: иначе цифра
            // молча исчезает, а карточка продолжает утверждать обратное.
          });
      }}
      className={`h-8 w-20 px-2 text-right text-xs tabular-nums ${stateClass}`.trim()}
      aria-label={`${crop.name} — ${MONTH_LABELS_SHORT[month - 1]}`}
    />
  );
}

export function PlanningSalePricesPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const validYear = Number.isFinite(year) && year >= 2000 && year <= 2100;
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [lastImport, setLastImport] = useState<ImportResult | null>(null);

  // Окно видимости — тот же ключ, что у хаба (`listSeasonCards(false)`), чтобы
  // не тянуть второй раз то, что хаб уже положил в кэш. Если года в окне нет —
  // добираем полный список (сезон вне окна открывается только по прямой ссылке).
  const seasonsWindowed = useQuery({
    queryKey: ['planning', 'season-cards', 'windowed'],
    queryFn: () => listSeasonCards(false),
    enabled: validYear,
    staleTime: 60_000,
  });
  const cardInWindow = useMemo(
    () => seasonsWindowed.data?.find((s) => s.year === year),
    [seasonsWindowed.data, year],
  );
  const needsFullList = validYear && !!seasonsWindowed.data && !cardInWindow;

  const seasonsAll = useQuery({
    queryKey: ['planning', 'season-cards', 'all'],
    queryFn: () => listSeasonCards(true),
    enabled: needsFullList,
    staleTime: 60_000,
  });

  const seasonCard = cardInWindow ?? seasonsAll.data?.find((s) => s.year === year);
  const seasonId = seasonCard?.season_id ?? null;
  const seasonsLoading =
    seasonsWindowed.isLoading || (needsFullList && seasonsAll.isLoading);
  const seasonsError = seasonsWindowed.isError || (needsFullList && seasonsAll.isError);
  const seasonsResolved = !seasonsLoading && !seasonsError && (!!cardInWindow || !!seasonsAll.data);

  const matrix = useQuery({
    queryKey: seasonId ? QUERY_KEY(seasonId) : ['planning', 'sale-prices', 'none'],
    queryFn: () => getSalePriceMatrix(seasonId as string),
    enabled: !!seasonId,
  });

  /**
   * Ячейки, которые не сохранились (403/409/сеть). Без этого сигнала цифра
   * остаётся в инпуте, будто уехала на бэк — рецидив UAT-бага #670.
   */
  const [failedCells, setFailedCells] = useState<PriceCellPatch[]>([]);
  const isSameCell = (a: PriceCellPatch, b: PriceCellPatch) =>
    a.crop_item_id === b.crop_item_id && a.month === b.month;

  const commit = useMutation({
    mutationFn: (cells: PriceCellPatch[]) => bulkUpsertSalePrices(seasonId as string, cells),
    onSuccess: (result, cells) => {
      setFailedCells((prev) => prev.filter((f) => !cells.some((c) => isSameCell(c, f))));
      if (!seasonId) return;
      queryClient.setQueryData(QUERY_KEY(seasonId), result.matrix);
    },
    onError: (_error, cells) => {
      setFailedCells((prev) => [...prev.filter((f) => !cells.some((c) => isSameCell(c, f))), ...cells]);
    },
  });
  const hasCellError = (cropItemId: string, month: number) =>
    failedCells.some((f) => f.crop_item_id === cropItemId && f.month === month);

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={validYear ? `Плановые цены — бюджет сезона ${year}` : 'Плановые цены'}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00).
            ...(validYear
              ? [
                  {
                    label: `Бюджет сезона ${year}`,
                    to: `/planning/seasons/${year}/budget`,
                  },
                ]
              : []),
            { label: 'Плановые цены' },
          ]}
          description="Матрица культура × месяц — формат прайса Гонтаревой. Импорт файла только предзаполняет, цифры свои, правятся руками."
          actions={
            seasonId ? (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Импорт из файла
              </Button>
            ) : undefined
          }
        />

        <Stack gap={4} className="mt-4">
          {!validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в url некорректный.
              </CardContent>
            </Card>
          )}

          {validYear && seasonsLoading && <Skeleton className="h-96 w-full" />}

          {validYear && seasonsError && (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-destructive">
                  Не удалось загрузить сезоны. Обновите страницу.
                </p>
              </CardContent>
            </Card>
          )}

          {/* AK-648: раньше оба случая («года нет в окне» и «сезон ещё не
              заведён») отвечались одним текстом, который отправлял на страницу
              планирования за кнопкой, которой там не было. */}
          {validYear && seasonsResolved && !seasonId && !seasonCard && (
            <SeasonYearOutOfWindowCard year={year} />
          )}
          {validYear && seasonsResolved && !seasonId && !!seasonCard && (
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {seasonId && (
            <>
              {failedCells.length > 0 && (
                <Card>
                  <CardContent className="flex flex-wrap items-center gap-3 p-4">
                    <p className="text-sm text-destructive">
                      Не сохранено ячеек: {failedCells.length}. Значения остались только на
                      экране — они подсвечены красным.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={commit.isPending}
                      onClick={() => commit.mutate(failedCells)}
                    >
                      {commit.isPending ? 'Повторяем…' : 'Повторить сохранение'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {lastImport && (
                <Card>
                  <CardContent className="flex items-start gap-2 p-4 text-sm">
                    <Info className="mt-1 h-4 w-4 shrink-0 text-primary" />
                    <div>
                      <p>
                        Импортировано: сопоставлено {lastImport.matched} культур, предзаполнено{' '}
                        {lastImport.cells_prefilled} ячеек
                        {lastImport.cells_skipped_manual > 0 &&
                          `, ${lastImport.cells_skipped_manual} защищено (уже были правки вручную)`}
                        .
                      </p>
                      {lastImport.unmatched.length > 0 && (
                        <p className="mt-1 text-muted-foreground">
                          Не сопоставлено (нет такой культуры в каталоге хозяйства):{' '}
                          {lastImport.unmatched.join(', ')}.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="p-4 pb-3">
                  <h2 className="text-sm font-semibold text-foreground">Цена реализации, ₽/т без НДС</h2>
                  <p className="text-xs text-muted-foreground">
                    Жёлтым — ячейки, изменённые вручную поверх импорта (или введённые
                    руками без импорта). «Средняя» — по фактически введённым месяцам,
                    это цена сценария по умолчанию.
                  </p>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {matrix.isLoading && <Skeleton className="h-64 w-full" />}
                  {matrix.isError && (
                    <p className="p-4 text-sm text-destructive">
                      Не удалось загрузить матрицу цен. Обновите страницу.
                    </p>
                  )}
                  {matrix.data && matrix.data.cultures.length === 0 && (
                    <p className="p-4 text-sm text-muted-foreground">
                      В каталоге хозяйства пока нет культур — заведите их в справочнике,
                      чтобы появились строки матрицы.
                    </p>
                  )}
                  {matrix.data && matrix.data.cultures.length > 0 && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="sticky left-0 z-20 bg-card">Культура</TableHead>
                            {MONTH_LABELS_SHORT.map((m) => (
                              <TableHead key={m} className="text-right">
                                {m}
                              </TableHead>
                            ))}
                            <TableHead className="text-right font-semibold">Средняя</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {matrix.data.cultures.map((crop) => {
                            const average = computeAverage(crop.months);
                            return (
                              <TableRow key={crop.crop_item_id}>
                                <TableCell className="sticky left-0 z-10 bg-card font-medium">
                                  {crop.name}
                                </TableCell>
                                {MONTH_LABELS_SHORT.map((_label, idx) => (
                                  <TableCell key={idx} className="text-right">
                                    <PriceCellInput
                                      crop={crop}
                                      month={idx + 1}
                                      hasError={hasCellError(crop.crop_item_id, idx + 1)}
                                      onCommit={(cropItemId, month, priceNoVat) =>
                                        // `mutateAsync` — ячейке нужен ПРОМИС:
                                        // по нему она решает, когда безопасно
                                        // уйти из режима правки без мигания.
                                        commit.mutateAsync([
                                          { crop_item_id: cropItemId, month, price_no_vat: priceNoVat },
                                        ])
                                      }
                                    />
                                  </TableCell>
                                ))}
                                <TableCell className="text-right font-semibold tabular-nums">
                                  {formatPrice(average)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <LogisticsPlaceholder />
            </>
          )}
        </Stack>
      </div>

      {importOpen && seasonId && (
        <ImportDialog
          seasonId={seasonId}
          onClose={() => setImportOpen(false)}
          onImported={(result) => {
            setLastImport(result);
            queryClient.setQueryData(QUERY_KEY(seasonId), result.matrix);
            setImportOpen(false);
          }}
        />
      )}
    </AppShell>
  );
}

/**
 * Логистика по направлениям — read-only плашка, без данных и без ввода.
 *
 * Схема `company_sale_prices` не несёт ось «направление» (`price_point` —
 * enum `warehouse|port` без разбивки на маршруты), поэтому CRUD здесь
 * невозможен: см. `docs/superpowers/specs/2026-08-06-sale-prices-form-open-questions.md` §1.
 *
 * Предыдущая версия рисовала правдоподобные, но выдуманные ставки и давала
 * по ним редактируемые инпуты, которые ничего не сохраняли — вырезано по
 * вердикту head-web (выдуманные цифры = ложный факт на UAT, нерабочее
 * редактирование = усталость от системы, CLAUDE.md §UX-3). Вернём блок
 * данными импортёра, когда architect/head-backend решат модель направлений.
 */
function LogisticsPlaceholder() {
  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            Логистика по направлениям, ₽/т сверх цены франко-поле
          </h2>
          <Badge variant="outline">в разработке</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground">
          Ставки логистики берутся из прайса Гонтаревой и подключаются импортёром.
          Раздел появится, когда справочник цен получит ось «направление» — сейчас
          маршруты к элеватору и порту схлопываются в одну запись, вводить их некуда.
        </p>
      </CardContent>
    </Card>
  );
}
